"use client";

import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { ShopPromotionListQueryDTO } from "@/modules/merchant/domain/contracts";
import {
  canUseMerchantPermission,
  type ShopPromotionListRow,
} from "@/modules/merchant/presentation/api/client";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import {
  ShopPromotionsFilter,
  type ShopPromotionsListSearch,
} from "@/modules/merchant/presentation/ui/ShopPromotionsFilter";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { RowActions } from "@/shared/ui/RowActions";
import {
  Badge,
  Button,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { EditIcon, PlusIcon } from "lucide-react";
import { useMemo } from "react";
import { useShopPromotionsListQuery } from "../api/queries";

const ROUTE = "/app/shops/$shopId/promotions" as const;

function statusBadge(s: ShopPromotionListRow["status"]): React.ReactNode {
  if (s === "active")
    return (
      <Badge className="bg-emerald-600 hover:bg-emerald-600">ໃຊ້ແລ້ວ</Badge>
    );
  if (s === "paused")
    return <Badge variant="secondary">ຢຸດຊົ່ວຄາວ</Badge>;
  return <Badge variant="outline">ຮ່າງ</Badge>;
}

function ruleLabel(r: ShopPromotionListRow["ruleType"]): string {
  return r === "line_percent_off" ? "% ຕໍ່ລາຍການ" : "ຈຳນວນຕໍ່ລາຍການ";
}

export function ShopPromotionsPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate({ from: ROUTE });
  const search = (useSearch({ from: ROUTE }) ?? {}) as ShopPromotionsListSearch;

  const offset = search.offset ?? 0;
  const limit = search.limit ?? 20;
  const statusQ: ShopPromotionListQueryDTO["status"] = search.status ?? "all";

  const { currentPermissions, currentShop } = useMerchantShops(shopId);
  const verifiedLevel = currentShop?.verifiedLevel ?? null;
  const canManage = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.promotions.manage,
    verifiedLevel,
  );

  const listParams: ShopPromotionListQueryDTO = useMemo(
    () => ({
      limit,
      offset,
      status: statusQ,
      search: search.search,
    }),
    [limit, offset, statusQ, search.search],
  );
  const listQ = useShopPromotionsListQuery(shopId, listParams);
  const rows = listQ.data?.data ?? [];
  const total = listQ.data?.meta.total ?? 0;

  const columns: TanstackReactTable.ColumnDef<ShopPromotionListRow>[] = useMemo(
    () => [
      {
        id: "name",
        header: "ຊື່",
        cell: ({ row }) => (
          <span className="font-medium">{row.original.name}</span>
        ),
      },
      {
        id: "status",
        header: "ສະຖານະ",
        cell: ({ row }) => statusBadge(row.original.status),
      },
      {
        accessorKey: "priority",
        header: "ລຳດັບ",
        cell: ({ row }) => (
          <span className="tabular-nums">{row.original.priority}</span>
        ),
      },
      {
        id: "rule",
        header: "ກົດ",
        cell: ({ row }) => {
          const r = row.original;
          const t = ruleLabel(r.ruleType);
          if (r.ruleType === "line_percent_off" && r.percentOff != null) {
            return `${t}: ${r.percentOff}%`;
          }
          if (r.ruleType === "line_amount_off" && r.amountOff != null) {
            return `${t}: ${r.amountOff}`;
          }
          return t;
        },
      },
      {
        id: "targets",
        header: () => <span className="block text-right">SKU</span>,
        cell: ({ row }) => (
          <span className="block text-right tabular-nums">
            {row.original.targetSkuIds.length}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        size: 120,
        cell: ({ row }) => {
          const r = row.original;
          if (!canManage)
            return null;
          return (
            <RowActions
              maxInline={1}
              actions={[
                {
                  label: "ແກ້ໄຂ",
                  icon: <EditIcon className="size-4" />,
                  onClick: () => {
                    if (!shopId) return;
                    nav({
                      to: "/app/shops/$shopId/promotions/$promotionId/edit",
                      params: { shopId, promotionId: r.id },
                    });
                  },
                },
              ]}
            />
          );
        },
      },
    ],
    [canManage, nav, shopId],
  );

  if (!shopId) return null;

  return (
    <RequireMerchantPermission
      permission={MerchantPermissions.promotions.read}
    >
      <Header>
        <Breadcrumb items={shopBreadcrumbs.promotions(shopId)} />
      </Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-semibold text-foreground text-lg">ໂປຣໂມຊັນ</h1>
            <p className="text-muted-foreground text-sm">
              ກົດລົບລາຄາອັດຕະໂນມັດຕາມ SKU ແລະຊ່ວງເວລາ (ຄິດໃນ POS ຕອນຊຳລະ)
            </p>
          </div>
          {canManage ? (
            <Button
              type="button"
              onClick={() => {
                if (!shopId) return;
                nav({
                  to: "/app/shops/$shopId/promotions/new",
                  params: { shopId },
                });
              }}
            >
              <PlusIcon className="mr-1 size-4" />
              ສ້າງໂປຣ
            </Button>
          ) : null}
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <ShopPromotionsFilter />
          <DataTable<ShopPromotionListRow, unknown>
            noDataMessage="ບໍ່ມີໂປຣທີ່ກົງກັບຕົວກອງ."
            isLoading={listQ.isLoading}
            columns={columns}
            data={rows}
            offset={offset}
            limit={limit}
            totalCount={total}
            onPaginationChange={(p) => {
              if (!shopId) return;
              nav({
                to: ROUTE,
                params: { shopId },
                search: {
                  offset: p.offset,
                  limit: p.limit,
                  status: search.status,
                  search: search.search,
                },
              });
            }}
          />
        </div>
      </Main>
    </RequireMerchantPermission>
  );
}
